/**
 * DemoConversionBanner — persistent banner shown to gated demo users.
 *
 * Displays time remaining and CTA to name their clinic (triggering
 * the silent org fork).  After the user claims an org, the banner
 * changes to show their clinic name.
 *
 * If the user provides a website URL, the backend crawls it during
 * the claim and returns extracted clinic data for confirmation.
 *
 * For expired demos, shows a "Start Free Trial" CTA instead.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Button, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RocketIcon from '@mui/icons-material/RocketLaunch';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useIsMobile } from '../../../hooks/useMediaQuery';
import { nowUtc, dayjs } from '../../../utils/dateUtils';
import { get, post } from '../../../utils/apiClient';
import WebsiteCrawlConfirmation from '../../setup-wizard/components/WebsiteCrawlConfirmation';
import CrawlWaitingOverlay from '../../setup-wizard/components/crawl/CrawlWaitingOverlay';
import useCrawlConfirmation from '../hooks/useCrawlConfirmation';
import { DEFAULT_ROLE_OPTIONS, DEFAULT_LICENSE_TYPES, DEFAULT_DEGREE_TYPES } from '../../setup-wizard/constants/staffDefaults';
import { allPasswordRulesMet } from '../../../constants/passwordRules';
import ClaimOrgDialog, { type SlugAvailability } from './ClaimOrgDialog';
import CrawlSuccessDialog from './CrawlSuccessDialog';

function formatTimeRemaining(expiresAt: string | null | undefined): string {
  if (!expiresAt) return '';
  const now = nowUtc();
  const expires = dayjs.utc(expiresAt);
  const diffMs = expires.valueOf() - now.valueOf();
  if (diffMs <= 0) return 'expired';
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  if (hours >= 24) {
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    return `${days}d ${remainingHours}h remaining`;
  }
  return `${hours}h ${minutes}m remaining`;
}

/** Urgency level based on remaining time: calm (>24h), warning (6-24h), urgent (<6h). */
type UrgencyLevel = 'calm' | 'warning' | 'urgent' | 'expired';

function getUrgencyLevel(expiresAt: string | null | undefined): UrgencyLevel {
  if (!expiresAt) return 'calm';
  const now = nowUtc();
  const expires = dayjs.utc(expiresAt);
  const diffMs = expires.valueOf() - now.valueOf();
  if (diffMs <= 0) return 'expired';
  const hours = diffMs / (1000 * 60 * 60);
  if (hours <= 6) return 'urgent';
  if (hours <= 24) return 'warning';
  return 'calm';
}

const URGENCY_COLORS: Record<UrgencyLevel, { bg: string; text: string; btnColor: string }> = {
  calm:    { bg: 'primary.main',  text: 'primary.contrastText', btnColor: 'primary.main' },
  warning: { bg: 'warning.dark',  text: '#fff',                 btnColor: 'warning.dark' },
  urgent:  { bg: 'error.main',    text: 'error.contrastText',    btnColor: 'error.main' },
  expired: { bg: 'error.main',    text: 'error.contrastText',    btnColor: 'error.main' },
};

interface DemoConversionBannerProps {
  /** Expose a function that external components can call to open the claim dialog */
  onRegisterOpenClaimDialog?: (opener: () => void) => void;
}

function DemoConversionBanner({ onRegisterOpenClaimDialog }: DemoConversionBannerProps) {
  const { user, refreshAuth } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  // Dismiss per browser session (sessionStorage) so the banner re-appears
  // on new visits — prospects who dismissed on Day 1 still see it on Day 2.
  const orgId = user && user.organization_id;
  const storageKey = orgId ? `claimBannerDismissed_${orgId}` : null;
  const hasClaimed = !!user && user.claimed_org === true;
  // The post-claim success banner is a one-time celebration on a REAL org:
  // once dismissed it must never return (localStorage, keyed to the user).
  const userKey = (user && (user.uuid || user.id)) || 'anon';
  const claimedKey = `claimedBannerDismissed_${orgId ?? 'org'}_${userKey}`;
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    if (hasClaimed) {
      try {
        if (localStorage.getItem(claimedKey) === '1') setDismissed(true);
      } catch { /* noop */ }
      return;
    }
    if (storageKey) {
      try {
        if (sessionStorage.getItem(storageKey) === '1') setDismissed(true);
      } catch { /* noop */ }
    }
  }, [storageKey, hasClaimed, claimedKey]);
  const dismiss = useCallback(() => {
    setDismissed(true);
    try {
      if (hasClaimed) {
        localStorage.setItem(claimedKey, '1');
      } else if (storageKey) {
        sessionStorage.setItem(storageKey, '1');
      }
    } catch { /* noop */ }
  }, [storageKey, hasClaimed, claimedKey]);
  const [timeStr, setTimeStr] = useState('');
  const [showClaimDialog, setShowClaimDialog] = useState(false);

  // Register the opener so siblings (e.g. DemoConversionPrompt) can trigger it
  useEffect(() => {
    if (onRegisterOpenClaimDialog) {
      onRegisterOpenClaimDialog(() => setShowClaimDialog(true));
    }
  }, [onRegisterOpenClaimDialog]);

  // Allow any component to open the claim dialog via a custom DOM event
  // (e.g. DemoSettingsCTA on locked settings tabs).
  useEffect(() => {
    const handler = () => setShowClaimDialog(true);
    window.addEventListener('open-claim-dialog', handler);
    return () => window.removeEventListener('open-claim-dialog', handler);
  }, []);
  // Restore form state saved before an OAuth redirect (if any).
  // Uses useState lazy initializer (not useMemo) because it performs a
  // side-effect (removeItem) and must run exactly once.
  const [savedClaimForm] = React.useState<Record<string, unknown> | null>(() => {
    const raw = sessionStorage.getItem('claim_org_form');
    if (raw) {
      sessionStorage.removeItem('claim_org_form');
      try { return JSON.parse(raw) as Record<string, unknown>; } catch { /* ignore */ }
    }
    return null;
  });

  // Auto-open the claim dialog when returning from an OAuth redirect with saved form data
  useEffect(() => {
    if (savedClaimForm) setShowClaimDialog(true);
  }, [savedClaimForm]);

  const [clinicName, setClinicName] = useState((savedClaimForm?.clinicName as string) ?? '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [claimErrorField, setClaimErrorField] = useState<'clinic' | 'password' | 'general'>('general');
  const [websiteUrl, setWebsiteUrl] = useState((savedClaimForm?.websiteUrl as string) ?? '');
  const [staffPageUrl, setStaffPageUrl] = useState((savedClaimForm?.staffPageUrl as string) ?? '');
  const [showStaffField, setShowStaffField] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [claimError, setClaimError] = useState('');
  const [legalAcknowledged, setLegalAcknowledged] = useState(Boolean(savedClaimForm?.legalAcknowledged));
  /** True while we're polling the background crawl after claim-org returned. */
  const [pollingCrawl, setPollingCrawl] = useState(false);

  // --- Slug state & auto-generation ---
  const [slug, setSlug] = useState((savedClaimForm?.slug as string) ?? '');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(Boolean(savedClaimForm?.slug));
  const [slugAvailability, setSlugAvailability] = useState<SlugAvailability>('idle');
  const [slugError, setSlugError] = useState('');
  const slugCheckTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Ref used to cancel the polling loop when the user dismisses the overlay. */
  const cancelPollingRef = useRef(false);

  const {
    crawlResult, showCrawlConfirmation, applyingCrawl,
    showSuccess, didConfirm, firstIncompleteStep,
    setCrawlFromResponse, handleConfirmCrawl, handleSkipCrawl, dismissSuccess,
    triggerSuccessNoCrawl,
  } = useCrawlConfirmation();

  /** Convert clinic name to a URL-friendly slug (kebab-case). */
  const slugifyName = useCallback((name: string): string => {
    let s = name.trim().toLowerCase();
    s = s.replace(/[^a-z0-9]+/g, '-');
    s = s.replace(/^-+|-+$/g, '');
    if (s.length > 60) s = s.slice(0, 60).replace(/-+$/, '');
    return s;
  }, []);

  /** Auto-generate slug when clinic name changes (unless user manually edited). */
  const handleClinicNameChangeWithSlug = useCallback((name: string) => {
    setClinicName(name);
    if (!slugManuallyEdited) {
      const generated = slugifyName(name);
      setSlug(generated);
      // Trigger availability check for the auto-generated slug
      setSlugAvailability(generated.length >= 3 ? 'checking' : 'idle');
      setSlugError('');
    }
  }, [slugManuallyEdited, slugifyName]);

  /** Handle manual slug edits — marks as manually edited. */
  const handleSlugChange = useCallback((value: string) => {
    setSlugManuallyEdited(true);
    setSlug(value);
    setSlugAvailability(value.length >= 3 ? 'checking' : 'idle');
    setSlugError('');
  }, []);

  /** Debounced slug availability check. */
  useEffect(() => {
    if (slugAvailability !== 'checking') return;
    if (slug.length < 3) {
      setSlugAvailability('idle');
      return;
    }
    let stale = false;
    if (slugCheckTimerRef.current) clearTimeout(slugCheckTimerRef.current);
    slugCheckTimerRef.current = setTimeout(async () => {
      try {
        const res = await get(`/api/orgs/check-slug-availability/${encodeURIComponent(slug)}`);
        const data = await res.json();
        if (stale) return; // Guard against stale response after slug changed
        if (data.available) {
          setSlugAvailability('available');
          setSlugError('');
        } else {
          setSlugAvailability(data.reason === 'Already taken' ? 'taken' : 'invalid');
          setSlugError(data.reason || 'Not available');
        }
      } catch {
        if (!stale) setSlugAvailability('idle');
      }
    }, 400);
    return () => {
      stale = true;
      if (slugCheckTimerRef.current) clearTimeout(slugCheckTimerRef.current);
    };
  }, [slug, slugAvailability]);

  const handleExploreDemo = useCallback(async () => {
    dismissSuccess();
    // Refresh auth so the new clinical_director role is picked up.
    // Wrapped in try/catch — the full page reload handles auth even
    // if the refresh endpoint fails (e.g. post-claim user without JWT).
    try { if (refreshAuth) await refreshAuth(); } catch { /* non-fatal */ }
    window.location.reload();
  }, [dismissSuccess, refreshAuth]);

  const handleCustomizePractice = useCallback(async () => {
    dismissSuccess();
    // Refresh auth so the new clinical_director role + claimed_org are picked
    // up by AuthContext before we navigate — otherwise the wizard page would
    // render with stale `user.claimed_org` and show the pre-claim banner.
    try { if (refreshAuth) await refreshAuth(); } catch { /* non-fatal */ }
    // Use React Router navigation (not `window.location.href`) so the user's
    // existing in-app tabs survive the hop.  Full page reloads trigger a
    // brief empty-tabs window that can race with the tab save effect.
    const stepParam = firstIncompleteStep > 0 ? `?step=${firstIncompleteStep}` : '';
    navigate(`/admin/setup-wizard${stepParam}`);
  }, [dismissSuccess, firstIncompleteStep, refreshAuth, navigate]);

  const isGatedDemo = !!user && user.is_gated_demo === true;
  const isOAuthUser = !!user && user.oauth_user === true;
  const consentAlreadyCompleted = !!user && user.signup_consent_completed === true;
  const expiresAt = user ? user.demo_expires_at : undefined;

  // Update countdown every minute
  useEffect(() => {
    if (!expiresAt) return;
    setTimeStr(formatTimeRemaining(expiresAt));
    const interval = setInterval(() => {
      setTimeStr(formatTimeRemaining(expiresAt));
    }, 60_000);
    return () => clearInterval(interval);
  }, [expiresAt]);

  /**
   * Poll ``GET /api/auth/crawl-status`` until the background crawl finishes.
   * When results arrive, show the crawl confirmation dialog.  On failure or
   * timeout (90 s) fall back to the no-crawl success path.
   */
  const pollForCrawlResults = useCallback(async () => {
    const MAX_POLLS = 30;        // 30 × 3 s = 90 s max
    const POLL_INTERVAL_MS = 3000;
    for (let i = 0; i < MAX_POLLS; i++) {
      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
      if (cancelPollingRef.current) { setPollingCrawl(false); return; }
      try {
        const res = await get('/api/auth/crawl-status');
        const body = await res.json();
        if (body.status === 'done' && body.crawl_data) {
          setPollingCrawl(false);
          const hasCrawl = setCrawlFromResponse({ crawl_data: body.crawl_data });
          if (!hasCrawl) triggerSuccessNoCrawl();
          return;
        }
        if (body.status === 'failed') break;
      } catch {
        // Network hiccup — keep trying
      }
    }
    // Timed out or failed — proceed without crawl data
    setPollingCrawl(false);
    triggerSuccessNoCrawl();
  }, [setCrawlFromResponse, triggerSuccessNoCrawl]);

  const handleClaimOrg = useCallback(async () => {
    if (!clinicName.trim()) {
      setClaimErrorField('clinic');
      setClaimError('Please enter your clinic name');
      return;
    }
    // OAuth users skip password validation (they authenticate via Google).
    if (!isOAuthUser) {
      if (!allPasswordRulesMet(password)) {
        setClaimErrorField('password');
        setClaimError('Password does not meet all requirements');
        return;
      }
      if (password !== confirmPassword) {
        setClaimErrorField('password');
        setClaimError('Passwords do not match');
        return;
      }
    }
    // Guard: don't submit if slug has a known error (defense-in-depth)
    if (slug.trim() && (slugAvailability === 'taken' || slugAvailability === 'invalid' || slugAvailability === 'checking')) {
      return;
    }
    // Guard: legal acknowledgment must be satisfied (checkbox or prior consent).
    if (!consentAlreadyCompleted && !legalAcknowledged) {
      return;
    }
    setClaiming(true);
    setClaimError('');
    try {
      const res = await post('/api/auth/claim-org', {
        clinic_name: clinicName.trim(),
        password: isOAuthUser ? undefined : password,
        website_url: websiteUrl.trim() || null,
        staff_page_url: staffPageUrl.trim() || null,
        url_slug: slug.trim() || null,
        legal_acknowledged: consentAlreadyCompleted || legalAcknowledged,
      }, { credentials: 'include' });

      let data: Record<string, unknown> = {};
      try {
        data = await res.json();
      } catch {
        // Response wasn't JSON — org was still created successfully
      }

      // Close the claim dialog immediately — org fork is done.
      setShowClaimDialog(false);
      setClaiming(false);

      // If the backend kicked off a background crawl, poll for results.
      // Do NOT call refreshAuth() here — it unmounts this component tree,
      // destroying the overlay state. The backend session is already updated
      // by claim-org (role → clinical_director, organization_id set), so
      // polling works without a frontend auth refresh.
      if (data.crawl_pending) {
        cancelPollingRef.current = false;
        setPollingCrawl(true);
        await pollForCrawlResults();
        return;
      }

      // No crawl requested — stash crawl data BEFORE refreshAuth (which
      // unmounts this tree, destroying React state). The hook reads
      // sessionStorage on remount to restore the dialog.
      const hasCrawl = setCrawlFromResponse(data);
      if (!hasCrawl) {
        triggerSuccessNoCrawl();
        if (refreshAuth) await refreshAuth();
      }
    } catch (err) {
      setClaimErrorField('general');
      setClaimError(
        err instanceof Error ? err.message : 'Failed to create your clinic. Please try again.'
      );
      setClaiming(false);
    }
  }, [clinicName, password, confirmPassword, websiteUrl, staffPageUrl, slug, slugAvailability, legalAcknowledged, consentAlreadyCompleted, isOAuthUser, refreshAuth, setCrawlFromResponse, triggerSuccessNoCrawl, pollForCrawlResults]);

  // Compute wizard completion percentage based on what the crawl filled.
  // The wizard has 10 steps (0-9). firstIncompleteStep tells us how far crawl got.
  const TOTAL_STEPS = 10;
  const completionPercent = didConfirm && firstIncompleteStep > 0
    ? Math.round((firstIncompleteStep / TOTAL_STEPS) * 100)
    : 0;

  // --- Modals: always rendered so they work even when the banner is dismissed ---
  const modals = (
    <>
      <ClaimOrgDialog
        open={showClaimDialog}
        onClose={() => setShowClaimDialog(false)}
        clinicName={clinicName}
        onClinicNameChange={handleClinicNameChangeWithSlug}
        slug={slug}
        onSlugChange={handleSlugChange}
        slugAvailability={slugAvailability}
        slugError={slugError}
        password={password}
        onPasswordChange={setPassword}
        confirmPassword={confirmPassword}
        onConfirmPasswordChange={setConfirmPassword}
        websiteUrl={websiteUrl}
        onWebsiteUrlChange={setWebsiteUrl}
        staffPageUrl={staffPageUrl}
        onStaffPageUrlChange={setStaffPageUrl}
        showStaffField={showStaffField}
        onToggleStaffField={() => setShowStaffField(true)}
        legalAcknowledged={legalAcknowledged}
        onLegalAcknowledgedChange={setLegalAcknowledged}
        claiming={claiming}
        claimError={claimError}
        claimErrorField={claimErrorField}
        onClearError={() => { setClaimError(''); setClaimErrorField('general'); }}
        onClaim={handleClaimOrg}
        isDemoMode={isGatedDemo}
        isOAuthUser={isOAuthUser}
        consentAlreadyCompleted={consentAlreadyCompleted}
      />
      <CrawlWaitingOverlay
        open={pollingCrawl}
        onClose={() => {
          // Signal the polling loop to stop on its next iteration.
          cancelPollingRef.current = true;
          setPollingCrawl(false);
          triggerSuccessNoCrawl();
        }}
      />
      {crawlResult && (
        <WebsiteCrawlConfirmation
          open={showCrawlConfirmation}
          data={crawlResult}
          onConfirm={handleConfirmCrawl}
          onSkip={handleSkipCrawl}
          loading={applyingCrawl}
          roleOptions={DEFAULT_ROLE_OPTIONS}
          licenseTypes={DEFAULT_LICENSE_TYPES}
          degreeTypes={DEFAULT_DEGREE_TYPES}
        />
      )}
      <CrawlSuccessDialog
        open={showSuccess}
        clinicName={clinicName || (user && user.clinic_name) || ''}
        didConfirm={didConfirm}
        onExploreDemo={handleExploreDemo}
        onCustomizePractice={handleCustomizePractice}
        completionPercent={completionPercent}
      />
    </>
  );

  // Only show banners for users who entered through the demo flow.
  // After claim-org the backend clears both is_gated_demo and demo_mode,
  // so we also check hasClaimed (which is set during the demo → real
  // transition) to keep the success banner visible until dismissed.
  // Direct-signup users have all three false → no banner.
  const isDemoUser = isGatedDemo || !!(user && user.demo_mode) || hasClaimed;
  if (!isDemoUser) return modals;
  if (dismissed) return modals;
  // The compact orange character banner owns the mobile clinic CTA.
  if (isMobile && !hasClaimed) return modals;

  // If user has claimed their org, show a success banner briefly
  if (hasClaimed) {
    return (
      <>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            px: 2,
            pr: { xs: 8, sm: 2 },
            py: 1,
            bgcolor: 'success.main',
            color: 'success.contrastText',
            position: 'relative',
            flexWrap: 'wrap',
          }}
        >
          <RocketIcon sx={{ fontSize: 20 }} />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Want to explore? Practice with{' '}
            <span
              onClick={() => navigate('/characters')}
              style={{ textDecoration: 'underline', cursor: 'pointer' }}
            >
              demo characters
            </span>{' '}
            to learn how the EHR works from different roles.
          </Typography>
          <IconButton
            size="small"
            onClick={dismiss}
            sx={{ color: 'success.contrastText', position: 'absolute', right: 8 }}
           aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        {modals}
      </>
    );
  }

  const expired = timeStr === 'expired';
  const urgency = getUrgencyLevel(expiresAt as string | null | undefined);
  const colors = URGENCY_COLORS[urgency];

  const urgentCopy = urgency === 'urgent'
    ? `Last chance! Only ${timeStr} — name your clinic before time runs out`
    : urgency === 'warning'
      ? `Time is running out — ${timeStr}`
      : timeStr
        ? `You're exploring the demo — ${timeStr}`
        : "You're exploring the demo";

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: isMobile ? 1 : 2,
          px: isMobile ? 1.5 : 2,
          py: isMobile ? 0.75 : 1,
          bgcolor: colors.bg,
          color: colors.text,
          position: 'relative',
          flexWrap: 'wrap',
        }}
      >
        <RocketIcon sx={{ fontSize: 20 }} />
        <Typography variant="body2" sx={{ fontWeight: urgency === 'urgent' ? 700 : 500, textAlign: 'center' }}>
          {expired ? 'Your demo has expired.' : urgentCopy}
        </Typography>
        {expired ? (
          <Button
            variant="contained"
            size="small"
            onClick={() => navigate('/name-my-clinic')}
            sx={{
              bgcolor: 'white',
              color: colors.btnColor,
              fontWeight: 700,
              '&:hover': { bgcolor: 'grey.100' },
              textTransform: 'none',
            }}
          >
            Start Free Trial
          </Button>
        ) : (
          <Button
            variant="contained"
            size="small"
            onClick={() => setShowClaimDialog(true)}
            sx={{
              bgcolor: 'white',
              color: colors.btnColor,
              fontWeight: 700,
              '&:hover': { bgcolor: 'grey.100' },
              textTransform: 'none',
            }}
          >
            {urgency === 'urgent' ? 'Name Your Clinic Now' : isMobile ? 'Name Your Clinic' : 'Name Your Clinic to Start Customizing'}
          </Button>
        )}
        {!expired && (
          <IconButton
            size="small"
            onClick={dismiss}
            sx={{ color: colors.text, position: 'absolute', right: 8, top: 4 }}
           aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      {modals}
    </>  
  );
}

export default DemoConversionBanner;
